import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import { DASHBOARD_CONSTANTS } from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type {
  TrafficEventListTodayItem,
  TrafficEventListTodayParams,
} from '../types/dashboard.types';

export interface TrafficEventListCard {
  key: string;
  /** ES document _id, for linking to GET /event/event/<id> - the
   *  event-details screen's detail unit. */
  id: string;
  title: string;
  address: string;
  dateTime: string;
  eventType: string;
  /** Source camera — null when the event carries no cctv_id (site-only rows). */
  cctvId: number | null;
}

// Loaded a page at a time as the operator scrolls, rather than pulling a whole
// day in one request: measured volume is 1.5k-5k events/day and has peaked at
// 13.5k, which is far more DOM than the panel can carry — and far more than the
// 2s live-refresh cadence can afford to re-pull.
const PAGE_SIZE = 200;

// The endpoint's own ceiling (`page * size <= 10000`, itself ES's
// max_result_window). Stopping here means the last "load more" ends the list
// instead of failing with a 400.
const MAX_FETCH = 10000;

// Render the WIB wall-clock regardless of the source offset. The ES timestamps
// come back in UTC (…+00:00), so parse to an instant and format in Asia/Jakarta
// rather than slicing the string (which would show the wrong hour).
const _wibFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatEventTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = _wibFormat.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('year')} · ${get('hour')}:${get('minute')}`;
};

// Index is part of the key on purpose. (timestamp, cctv, type) is NOT unique —
// 17 of one sample day's 1842 events collide on it, two distinct events having
// been detected at the same camera in the same second. Deduping on that
// identity would silently drop real rows, so the list keeps them and the
// position disambiguates the key.
const toCard = (e: TrafficEventListTodayItem, index: number): TrafficEventListCard => {
  const title = e.count > 1 ? `${e.event_label} ×${e.count}` : e.event_label;
  const place = e.cctv_name ?? (e.site_id ? `Site ${e.site_id}` : '-');
  const address = e.location_label ? `${place} · ${e.location_label}` : place;
  return {
    key: `${index}|${e.timestamp}|${e.cctv_id ?? '-'}|${e.event_type}`,
    id: e.id,
    title,
    address,
    eventType: e.event_type,
    dateTime: formatEventTime(e.timestamp),
    cctvId: e.cctv_id,
  };
};

export const useTrafficEventList = (params: TrafficEventListTodayParams = {}) => {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['trafficEventListToday', params],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        dashboardApi.getTrafficEventListToday({ ...params, page: pageParam, size: PAGE_SIZE }),
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage?.data?.total_count ?? 0;
        const loaded = allPages.reduce((n, page) => n + (page?.data?.events?.length ?? 0), 0);
        // A short page means the server has nothing further; guarding on `loaded`
        // alone would loop forever if it ever returned fewer rows than it counted.
        if (loaded === 0 || loaded >= total) return undefined;
        const nextPage = allPages.length + 1;
        return nextPage * PAGE_SIZE > MAX_FETCH ? undefined : nextPage;
      },
      staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
      gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
      // Live feed cadence, but only while the newest page is the *only* page.
      // React Query refetches every loaded page on each tick, so once history is
      // loaded a 2s interval would fire one request per page — and resorting the
      // list under someone who is reading older events is wrong regardless.
      // Changing the date resets the query, which restores the live cadence.
      refetchInterval: (query) =>
        (query.state.data?.pages.length ?? 1) > 1
          ? false
          : DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
      placeholderData: (prev) => prev,
    });

  const events = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page?.data?.events ?? []),
    [data?.pages]
  );

  return {
    events,
    cards: useMemo(() => events.map(toCard), [events]),
    // Server-side total for the whole window — not the number loaded so far.
    totalCount: data?.pages?.[0]?.data?.total_count ?? 0,
    totalOccurrences: data?.pages?.[0]?.data?.total_occurrences ?? 0,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
  };
};
